import { DashboardState, EScreenType, ESdChangeReason, IScreenDesign, IScreenDesignInfo } from "@kuika/kuika-cl-model";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface ReportDesignProviderState {
  isReportDesignPageSelected: () => boolean;
  setVisibilityOfCreateReportDesignPage: (value: boolean) => void;
  isCreateReportDesignPageActive: boolean;
  isReportDesignPageExist: () => boolean;
  isReportDesignerLandingVisible: () => boolean;
  isLoadedScreenReport: () => boolean;
}

export class ReportDesignProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ReportDesignProviderState
> {
  static defaultState: ReportDesignProviderState = {
    isReportDesignPageSelected: () => false,
    setVisibilityOfCreateReportDesignPage: () => {},
    isCreateReportDesignPageActive: false,
    isReportDesignPageExist: () => false,
    isReportDesignerLandingVisible: () => false,
    isLoadedScreenReport: () => false
  };

  getProviderValues = (): ReportDesignProviderState => {
    return {
      isReportDesignPageSelected: this.isReportDesignPageSelected,
      setVisibilityOfCreateReportDesignPage: this.setVisibilityOfCreateReportDesignPage,
      isCreateReportDesignPageActive: this.state.isCreateReportDesignPageActive,
      isReportDesignPageExist: this.isReportDesignPageExist,
      isReportDesignerLandingVisible: this.isReportDesignerLandingVisible,
      isLoadedScreenReport: this.isLoadedScreenReport
    };
  };

  /**
   * Whether the Report Designer's landing owns the centre. It does when the user asked for the list
   * (`isCreateReportDesignPageActive`) and, whatever that flag says, whenever the loaded screen is
   * not a report: the report canvas never paints a UI screen, so an entry path that forgets the flag
   * can no longer leave one standing there with no report selected.
   *
   * "Report" is either marker — the loaded tree's `isAbsoluteDesign`, which is what project load
   * keys report mode on, or the screen list's type — so a report carrying only one of them is never
   * locked under the landing. The loaded tree and `sdId` are read rather than the URL: the URL moves
   * the moment a load starts, the tree only when it lands, and until then the landing stays up.
   */
  isReportDesignerLandingVisible = (): boolean => {
    if (this.state.dashboardState !== DashboardState.reportDesigner) {
      return false;
    }
    if (this.state.isCreateReportDesignPageActive) {
      return true;
    }
    return !this.isLoadedScreenReport();
  };

  /**
   * Whether the screen currently LOADED is a report — either marker, tree first.
   *
   * Public because the UI-design surfaces need it too: the device menu writes a viewport width
   * onto whatever screen is open, and `dashboardState` cannot tell them to stop. The tool
   * switches to `design` before the screen does, so on the way out of the Report Designer the
   * report is still the loaded screen and used to take a phone width. Read the tree and `sdId`
   * rather than the URL (`isReportDesignPageSelected`): the URL moves the moment a load starts.
   */
  isLoadedScreenReport = (): boolean => {
    const screenDesign: IScreenDesign | undefined = this.partial.screenProvider.getScreenDesign();
    if (screenDesign?.isAbsoluteDesign === true) {
      return true;
    }
    const { sdId, screenList } = this.state;
    const loadedScreen: IScreenDesignInfo | undefined = (screenList ?? []).find(
      (screen: IScreenDesignInfo): boolean => screen.id === sdId
    );
    return loadedScreen?.publicMetaData?.screenType === EScreenType.Report;
  };

  /**
   * `changeReason` is load-bearing: this flag decides whether the Report Designer's landing
   * page or the canvas owns the centre, and `CanvasFrame` — a `PureSdComponent` whose own
   * gate returns false — would otherwise never re-run its resize and would leave the design
   * iframe (a body-level element) showing through the landing page.
   */
  setVisibilityOfCreateReportDesignPage = (value: boolean) => {
    this.setState({ isCreateReportDesignPageActive: value, changeReason: ESdChangeReason.OtherSetStates });
  };

  isReportDesignPageSelected = () => {
    const selectedSdId = KMainFunctions.getSdIdFromUrl();
    const { screenList } = this.state;
    const selectedScreen = screenList.find((screen) => screen.id === selectedSdId);
    if (selectedScreen && selectedScreen.publicMetaData.screenType === EScreenType.Report) {
      return true;
    }
    return false;
  };

  isReportDesignPageExist = () => {
    const { screenList } = this.state;
    const reportDesignPage = screenList.find((screen) => screen.publicMetaData.screenType === EScreenType.Report);
    if (reportDesignPage) {
      return true;
    }
    return false;
  };
}
